import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import FilterProvider, {
  FilterState,
} from "./(overview)/(context)/filter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import React from "react";
import { formatDate } from "@/lib/date";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import ViewTaskSubmission from "@/components/features/tasks/view-submission";
import { filterObjectsByValue } from "@/lib/filter";
import RemoveTask from "@/components/features/tasks/remove";
import UpdateTask from "@/components/features/tasks/update";
import SelectUser from "@/components/features/users/select-user";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import PriorityFormat from "@/components/features/tasks/priority-format";
import { useNavigate } from "react-router-dom";
import { ROUTES_CONFIG } from "@/config/routes.config";

type Props = {
  onRefetchReady?: (fn: () => void) => void;
};

const AllTasks = ({ onRefetchReady }: Props) => {
  const navigate = useNavigate();
  const { trpc } = useTRPC();
  const { watch, setValue } = useFormContext<FilterState>();
  const filterState = watch();

  const [search, setSearch] = React.useState<string>('');

  const query = useQuery({
    queryKey: ["tasks", filterState],
    queryFn: async () => {
      const tasks = await trpc.tasks.search.query({});
      return tasks;
    },
    enabled: false,
  });

  React.useEffect(() => {
    onRefetchReady?.(() => query.refetch());
  }, [query.refetch]);

  React.useEffect(() => {
    query.refetch();
  }, [filterState.page]);

  const updateAssignee = useMutation({
    mutationFn: async (data: { task_id: string, assigned_user_id: string }) => {
      await trpc.tasks.update.mutate({
        task_id: data.task_id,
        assigned_user_id: data.assigned_user_id
      })
    },
    onSuccess: () => {
      query.refetch();
    },
    onError: (err) => {
      if (err instanceof Error) {
        toast.error(err.message);
      } else if (err instanceof TRPCClientError) {
        toast.error(err.message);
      } else {
        toast.error("Er is een onbekende fout opgetreden.");
      }
    }
  })

  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between h-full gap-4">
        <div className="flex items-center justify-between gap-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Zoek naar taak"
            startContent={<Icons.Search className="w-4 h-4" />}
            className="w-fit"
          />
        </div>
        <div className="bg-foreground/10 rounded-md border pb-2 pr-2 pl-2 overflow-auto h-full">
          <Table className='overflowclip w-full relative' divClassname='overflow-y-scroll max-h-[100%]'>
            <TableHeader className="sticky top-0 w-full border-b bg-sidebar/50 backdrop-blur-md z-10">
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Type rapport</TableHead>
                <TableHead>Speler</TableHead>
                <TableHead>Aangewezen scout</TableHead>
                <TableHead>Prioriteit</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Aangeboden door</TableHead>
                <TableHead>Notitie voor scout</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!query.isLoading && query?.data?.tasks?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-4">
                    Er zijn geen taken aanwezig.
                  </TableCell>
                </TableRow>
              ) : (
                filterObjectsByValue(query?.data?.tasks || [], search).map((task, i) => (
                  <ViewTaskSubmission
                    task_id={task.id}
                    disabled={!task?.completed}
                    trigger={
                      <TableRow key={i} className={cn("group/row",
                        task?.completed && "hover:bg-input/50 cursor-pointer"
                      )}>
                        <TableCell>
                          {task?.completed
                            ? <Icons.CircleCheck className="w-4 h-4 text-emerald-500" />
                            : <Icons.Hourglass className="w-4 h-4 text-orange-500" />
                          }
                        </TableCell>
                        <TableCell>
                          <p
                            className={cn(
                              "font-medium",
                              task?.completed && "hover:underline cursor-pointer"
                            )}
                          >
                            {task?.report?.type?.join(" - ")}
                          </p>
                        </TableCell>
                        <TableCell
                          onClick={() => {
                            if (task?.mongo_player?.tmId === undefined) return;
                            navigate(
                              ROUTES_CONFIG.APP.PLAYER_DETAIL.$buildPath({
                                params: { player_id: task.mongo_player.id },
                              })
                            );
                          }}
                          className="cursor-pointer hover:underline"
                        >
                          {task?.mongo_player?.name}
                        </TableCell>
                        <TableCell>
                          {task?.completed
                            ?
                            <p className="text-center">{task?.assigned_user?.first_name} {task?.assigned_user?.last_name}</p>
                            :
                            <SelectUser
                              value={task?.assigned_user_id}
                              onValueChange={(v) => {
                                if (!v) {
                                  toast.error("Gelieve een gebruiker te selecteren.");
                                  return;
                                }
                                updateAssignee.mutate({
                                  task_id: task.id,
                                  assigned_user_id: v
                                })
                              }}
                            />
                          }
                        </TableCell>
                        <TableCell>
                          <PriorityFormat
                            priority={task?.priority}
                          />
                        </TableCell>
                        <TableCell>{task?.expiration_date ? formatDate(task?.expiration_date) : ''}</TableCell>
                        <TableCell>{task?.offered_by?.contact_name}</TableCell>
                        <TableCell>{task?.note}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger>
                                <Icons.MoreVertical className="w-4 h-4" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                {!task.completed &&
                                  <UpdateTask
                                    task_id={task.id}
                                    defaultValues={{
                                      assigned_user_id: task?.assigned_user_id,
                                      club_id: typeof task?.mongo_player?.club === 'string' ? task?.mongo_player?.club : undefined,
                                      mongo_player_id: task?.mongo_player_id ?? undefined,
                                      report_id: task?.report_id ?? undefined,
                                      source: task?.source ?? undefined,
                                      offered_by_id: task?.offered_by_id ?? undefined,
                                      note: task?.note ?? undefined,
                                      priority: task?.priority ?? undefined,
                                      expiration_date: task?.expiration_date ?? undefined,
                                      ext_competition_id: task?.ext_competition_id ?? undefined,
                                      ext_club_id: task?.ext_club_id ?? undefined,
                                    }}
                                    onUpdated={() => {
                                      query.refetch();
                                    }}
                                    trigger={
                                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                        <Icons.Edit className="w-4 h-4" />
                                        Bewerken
                                      </DropdownMenuItem>
                                    }
                                  />
                                }
                                <RemoveTask
                                  task_id={task.id}
                                  onRemoved={() => {
                                    query.refetch();
                                  }}
                                  trigger={
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive hover:!text-destructive/80">
                                      <Icons.Trash className="w-4 h-4 text-destructive" />
                                      Verwijderen
                                    </DropdownMenuItem>
                                  }
                                />
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total || 0} rijen gevonden
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
              }
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              value={filterState.page}
              onChange={(e) => {
                setValue("page", e.currentTarget.valueAsNumber);
              }}
              onBlur={() => {
                query.refetch();
              }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                  )
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({ ...props }: Props) => {
  return (
    <FilterProvider>
      <AllTasks {...props} />
    </FilterProvider>
  );
};
